import React, { useEffect, useRef, useState } from "react";
import { FaFileAlt, FaUndoAlt } from "react-icons/fa";
import { readStoredUser } from "../../../utils/sessionUser";
import PurchasingDocumentDialog from "./PurchasingDocumentDialog";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import { ocrReopenText } from "./ocrReopenReviewMessages";
import "./OcrReopenReviewButton.css";

export const isCreatedOcrImport = (record) => Boolean(record?.created_delivery_note_id)
  || ["created", "confirmed"].includes(record?.status);
const canReopen = (user) => Number(user?.id) === 1 || user?.permissions?.access_settings === true
  || ["create", "update"].every((action) => user?.permissions?.[`purchasing.delivery_notes.${action}`] === true);

export default function OcrReopenReviewButton({ importData, scopedApi, onReopened, disabled = false }) {
  const { languageId } = useReceivingPopupUi();
  const t = (message, values) => ocrReopenText(languageId, message, values);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errorDetail, setErrorDetail] = useState("");
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const cancelled = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const current = () => mounted.current && scopedApi.isCurrent() && canReopen(readStoredUser());
  const reopen = async () => {
    if (inFlight.current || disabled) return;
    if (!current()) { setError("Reopening requires permission to create and edit delivery notes in the current business unit."); return; }
    inFlight.current = true;
    setBusy(true); setError(""); setErrorDetail("");
    const noteId = importData.created_delivery_note_id == null ? null : Number(importData.created_delivery_note_id);
    const url = `/api/purchasing/ocr-imports/${importData.id}/reopen`;
    const payload = { expected_delivery_note_id: noteId };
    try {
      let response;
      try { response = await scopedApi.post(url, payload); }
      catch (failure) {
        // The server verifies the association before any cancellation. A stale
        // tab must never cancel a newer replacement generated by another user.
        const resolvedId = Number(failure.response?.data?.delivery_note_id);
        if (failure.response?.data?.code !== "OCR_REOPEN_NOTE_ACTIVE" || !Number.isSafeInteger(resolvedId) || resolvedId <= 0
          || (noteId !== null && resolvedId !== noteId)) throw failure;
        if (!current()) return;
        const cancellation = await scopedApi.post(`/api/purchasing/receivings/delivery-notes/${resolvedId}/cancel`, {
          reason: `Reopening OCR import #${importData.id} to correct the review`,
        });
        if (cancellation.data?.status !== "cancelled" || cancellation.data?.success !== true) throw new Error("Cancellation unconfirmed");
        cancelled.current = true;
        window.dispatchEvent(new CustomEvent("refreshDeliveryNotesList"));
        if (!current()) return;
        response = await scopedApi.post(url, payload);
      }
      if (!current()) return;
      if (!response.data?.success || !response.data?.import || isCreatedOcrImport(response.data.import)) throw new Error("Reopening unconfirmed");
      setOpen(false);
      onReopened(response.data.import);
      window.dispatchEvent(new CustomEvent("refreshDeliveryNotesList"));
    } catch (failure) {
      if (mounted.current) {
        setError(failure.response?.data?.code === "OCR_REOPEN_CHANGED"
        ? "This import has changed. Close the review and reload the inbox before continuing."
        : cancelled.current ? "The previous note is cancelled, but the review could not be reopened. Retry to recover it using the same document."
        : "Reopening could not be completed. Retry to check the previous note and resume safely.");
        // Business errors explain which permission, stock or document condition
        // blocks cancellation. Never expose unexpected server failure details.
        if (failure.response?.status >= 400 && failure.response.status < 500
          && typeof failure.response.data?.error === "string") setErrorDetail(failure.response.data.error);
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  if (!isCreatedOcrImport(importData) || !canReopen(readStoredUser())) return null;
  return <>
    <button type="button" className="ocrp-btn ocrp-reopenButton" disabled={disabled || busy}
      onClick={(event) => { event.stopPropagation(); setOpen(true); }}><FaUndoAlt aria-hidden="true" /><span>{t("Reopen review")}</span></button>
    <PurchasingDocumentDialog isOpen={open} title={t("Reopen import #{id}?", { id: importData.id })}
      message={<div className="ocrp-reopenDetails">
        <div className="ocrp-reopenStep"><FaUndoAlt aria-hidden="true" /><div>
          <strong>{t("Previous delivery note")}</strong>
          <p>{t("If it still exists, it will be cancelled and its posted stock reversed. If it was already deleted, the review will simply be recovered.")}</p>
        </div></div>
        <div className="ocrp-reopenStep"><FaFileAlt aria-hidden="true" /><div>
          <strong>{t("Same document, new draft")}</strong>
          <p>{t("Keep the original and your saved review. Correct the supplier or items, then create a new draft. No need to upload or scan again.")}</p>
        </div></div>
        {error && <p role="alert">{t(error)}</p>}
        {errorDetail && <p>{errorDetail}</p>}
      </div>}
      isBusy={busy} confirmLabel={t(busy ? "Reopening…" : error ? "Retry reopening" : "Confirm reopening")}
      cancelLabel={t("Keep as is")} onConfirm={reopen} onClose={() => { if (!inFlight.current) setOpen(false); }} tone="warning" />
  </>;
}
